import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders,} from '@angular/common/http';
import {catchError, Observable, of, tap, throwError} from 'rxjs';
import {Login} from './auth.interface';
import {environment} from '@/environments/environment';
import {TokenService} from './token.service';
import {UserService} from './user.service';
import {UserRoleStore} from '@/app/core/states/user-role-stare/client-role-state.store';
import {Router} from '@angular/router';
import {SnackbarService} from '@/app/shared/services/snackbar.service';
import { ClientStore } from '@/app/core/states/clientes-state/client-state.store';
import { ClientsService } from '@/app/features/clientes/services/clients.service';

@Injectable({providedIn: 'root'})
export class AuthService {
  private apiGatewayUrl = environment.apiGatewayUrl;
  private tokenService = inject(TokenService);
  private userService = inject(UserService);
  private router = inject(Router);
  private userState = inject(UserRoleStore);
  private snackbarService = inject(SnackbarService);
  private clientStore = inject(ClientStore);
  private clientsService = inject(ClientsService);

  constructor(private http: HttpClient) {
  }

  login(credentials: { email: string; password: string }): Observable<any> {
    const loginRequest: Login = {
      username: credentials.email,
      password: credentials.password,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post(`${this.apiGatewayUrl}/account/login`, loginRequest, {
        headers,
      })
      .pipe(
        tap((response: any) => {
          if (response.accessToken && response.refreshToken) {
            this.tokenService.setTokens(
              response.accessToken,
              response.refreshToken
            );
            const userId = this.tokenService.decodeToken(response.accessToken);
            this.fetchUserDetails(userId);
          }
        }),
        catchError(this.handleError)
      );
  }

  logout(sessionExpired: boolean = false): void {
    // Limpiar tokens de autenticación
    this.tokenService.clearTokens();

    // Limpiar timer de auto-refresh
    if (this.tokenService['refreshTimer']) {
      clearTimeout(this.tokenService['refreshTimer']);
    }

    this.userState.resetData(); // Limpia stores y localStorage

    if (sessionExpired) {
      this.snackbarService.showError('Su sesión ha expirado. Por favor, vuelva a iniciar sesión.');
    }

    // Navegar y recargar para un reseteo completo
    this.router.navigate(['/iniciar']);
  }

  isAuthenticated(): boolean {
    return this.tokenService.hasValidToken();
  }

  checkAndRefreshToken(): Observable<any> {
    if (
      this.tokenService.getAccessToken() &&
      this.tokenService.isTokenExpired(this.tokenService.getAccessToken()!)
    ) {
      return this.tokenService.refreshToken().pipe(
        tap((response: any) => {
          if (response.accessToken) {
            this.tokenService.setTokens(
              response.accessToken,
              response.refreshToken
            );
          } else {
            this.logout(true); // Sesión expirada
          }
        }),
        catchError(() => {
          this.logout(true); // Sesión expirada
          return throwError(() => new Error('Refresh failed'));
        })
      );
    }
    return of(null);
  }

  private handleError(error: HttpErrorResponse) {
    console.error('An error occurred:', error);
    return throwError(() => error);
  }

  private fetchUserDetails(userId: string): void {
    this.userService.getUserDetails(userId).subscribe({
      next: (user) => {
        this.userState.setUser(user);

        const normalizedUser = this.userState.user();
        const clientId = normalizedUser?.attributes?.['publicId'] || normalizedUser?.id;

        if (normalizedUser?.group !== 'ADMIN' && clientId) {
          this.clientsService.getClient(clientId).subscribe(client => {
            if (client) {
              this.clientStore.setClient(client);
            }
          });
        }

        if (normalizedUser?.group === 'ADMIN') {
          this.router.navigate(['/inicio']);
        } else {
          this.router.navigate(['/campaña']);
        }
      },
      error: (err) => {
        console.error('Error fetching user details:', err);
      },
    });
  }
}
